import { useEffect, useState } from 'react';
import type { Adjunto, EntidadAdjunto } from '@matias-asset-os/contracts';
import { formatearFecha, t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { Icono } from './Iconos.js';

/**
 * FOTOS.
 *
 * Una obra se reconoce por su foto mucho antes que por su dirección, y cómo va
 * se ve mirando la última al lado de la de hace un mes. Una tarea con una foto
 * del cuadro eléctrico se entiende sin leer el comentario que la explica.
 *
 * CADA FOTO DICE QUIÉN Y CUÁNDO, y eso es la mitad del valor: «la grúa ya no
 * está» sólo significa algo con una fecha al lado, y a quien la sacó es a quien
 * se le pregunta. Por eso la lista va por fecha descendente: lo último que se
 * vio es lo que cuenta el estado de hoy.
 *
 * Se aceptan también PDF y documentos —una ficha escaneada llega así— pero lo
 * que no es imagen se enseña como un enlace con su nombre, en vez de dejar un
 * cuadro roto.
 */

interface Props {
  idioma: Idioma;
  entidad: EntidadAdjunto;
  entidadId: string;
  /** Sin esto la galería es de mirar: ni se sube ni se quita. */
  puedeGestionar: boolean;
  titulo?: string;
}

const ADMITIDOS = 'image/jpeg,image/png,image/webp,image/heic,application/pdf';

function esImagen(a: Adjunto): boolean {
  return a.tipoMime.startsWith('image/');
}

export function GaleriaFotos({ idioma, entidad, entidadId, puedeGestionar, titulo }: Props) {
  const [fotos, setFotos] = useState<Adjunto[] | null>(null);
  const [subiendo, setSubiendo] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .adjuntos(entidad, entidadId)
      .then((r) => {
        if (vivo) setFotos(r.adjuntos);
      })
      .catch(() => {
        if (vivo) setFotos([]);
      });
    return () => {
      vivo = false;
    };
  }, [entidad, entidadId]);

  async function subir(elegidos: FileList | null) {
    const lista = [...(elegidos ?? [])];
    if (lista.length === 0) return;
    setError(null);
    setSubiendo(lista.length);
    for (const fichero of lista) {
      try {
        const { adjunto } = await api.subirAdjunto(entidad, entidadId, fichero);
        // Delante: la última foto es la que cuenta cómo está hoy.
        setFotos((antes) => [adjunto, ...(antes ?? [])]);
      } catch {
        setError(t(idioma, 'fotos.noSeSubio', { fichero: fichero.name }));
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  }

  async function quitar(foto: Adjunto) {
    if (!confirm(t(idioma, 'fotos.confirmarQuitar'))) return;
    try {
      await api.eliminarAdjunto(foto.id);
      setFotos((antes) => (antes ?? []).filter((f) => f.id !== foto.id));
    } catch {
      setError(t(idioma, 'fotos.noSeQuito'));
    }
  }

  return (
    <section className="tarjeta">
      <div className="tarjeta-cabeza">
        <h2>
          {titulo ?? t(idioma, 'fotos.titulo')}
          {fotos && fotos.length > 0 && <span className="contador"> {fotos.length}</span>}
        </h2>
        {puedeGestionar && (
          <label className="enlace subir-fotos">
            <Icono nombre="camara" /> {t(idioma, 'fotos.subir')}
            <input
              type="file"
              accept={ADMITIDOS}
              multiple
              onChange={(e) => {
                void subir(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {subiendo > 0 && (
        <p className="suave" role="status">
          {t(idioma, 'fotos.subiendo', { total: subiendo })}
        </p>
      )}

      {fotos === null && <p className="suave">{t(idioma, 'app.cargando')}</p>}
      {fotos !== null && fotos.length === 0 && (
        <p className="suave">
          {t(idioma, puedeGestionar ? 'fotos.vacioPuede' : 'fotos.vacio')}
        </p>
      )}

      {fotos !== null && fotos.length > 0 && (
        <div className="galeria">
          {fotos.map((foto) => (
            <figure key={foto.id} className="galeria-foto">
              <a href={api.urlAdjunto(foto.id)} target="_blank" rel="noreferrer">
                {esImagen(foto) ? (
                  <img src={api.urlAdjunto(foto.id)} alt={foto.nombreFichero} loading="lazy" />
                ) : (
                  <span className="galeria-doc">
                    <Icono nombre="documento" />
                    {foto.nombreFichero}
                  </span>
                )}
              </a>
              {/*
                QUIEN Y CUANDO. Sin esto, dos fotos de la misma fachada no dicen
                si la obra avanza o si alguien subio dos veces la misma.
              */}
              <figcaption>
                <b>{foto.subidoPorNombre ?? t(idioma, 'fotos.sinAutor')}</b>
                <small>{formatearFecha(new Date(foto.creadoEn), idioma)}</small>
              </figcaption>
              {puedeGestionar && (
                <button
                  type="button"
                  className="icono-accion peligro galeria-quitar"
                  title={t(idioma, 'fotos.quitar')}
                  aria-label={t(idioma, 'fotos.quitar')}
                  onClick={() => void quitar(foto)}
                >
                  <Icono nombre="papelera" />
                </button>
              )}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
